import React, { useState, useEffect } from 'react';
import { X, Smartphone, Monitor } from 'lucide-react';

const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if banner was dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      return;
    }

    // Detect mobile
    const checkMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(checkMobile);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the install banner
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowBanner(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`User response to the install prompt: ${outcome}`);

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowBanner(false);

    // Remember the choice
    if (outcome === 'accepted') {
      localStorage.setItem('pwa-install-dismissed', 'installed');
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
      style={{
        background: 'linear-gradient(135deg, #F5EDE3 0%, #FAF7F2 100%)',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(189, 162, 122, 0.2), 0 2px 8px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(189, 162, 122, 0.2)'
      }}
    >
      <div className="p-5">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/5 transition-colors"
          aria-label="Закрыть"
        >
          <X className="h-4 w-4" style={{ color: '#8A7F72' }} />
        </button>

        {/* Content */}
        <div className="pr-6">
          <h3 
            className="text-lg font-serif mb-2"
            style={{ 
              color: '#2F2F2F',
              fontFamily: 'Cormorant Garamond, serif',
              fontWeight: 600
            }}
          >
            Установить EUNOIA как приложение
          </h3>
          
          <p 
            className="text-sm mb-4"
            style={{ color: '#8A7F72', lineHeight: '1.5' }}
          >
            Получите быстрый доступ к событиям и сообществу прямо с главного экрана
          </p>

          {/* Install buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleInstallClick}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all duration-300"
              style={{
                background: '#BDA27A',
                color: 'white',
                fontWeight: 500,
                fontSize: '14px',
                boxShadow: '0 2px 8px rgba(189, 162, 122, 0.25)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#A88C65';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(189, 162, 122, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#BDA27A';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(189, 162, 122, 0.25)';
              }}
            >
              {isMobile ? (
                <>
                  <Smartphone className="h-4 w-4" />
                  <span>На телефон</span>
                </>
              ) : (
                <>
                  <Monitor className="h-4 w-4" />
                  <span>На компьютер</span>
                </>
              )}
            </button>
          </div>

          {/* Info text */}
          <p 
            className="text-xs mt-3 text-center"
            style={{ color: '#A89B8E' }}
          >
            Работает без интернета
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default InstallBanner;
